import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Button, Alert, message } from 'antd';
import { EditOutlined, MailOutlined, MobileOutlined } from '@ant-design/icons';
import ProfileAvatar from './ProfileAvatar';
import EditProfileModal from './EditProfileModal';
import MemberCard from './MemberCard';
import { useTranslation } from 'react-i18next';
import { BASE_URL, LOGION_URL, OTP_URL, UPDATE_PHONE_URL } from '../../constants/Url';
import { FaRegEdit } from "react-icons/fa";
import PhoneModal from '../auth/PhoneModal';
import EmailModal from '../auth/EmailModal';
import PhoneInput from '../InputField/PhoneInput';
import { loginSuccessAction } from '../../modules/auth/actions';
import httpClient from '../../utils/HttpClient';
import { useDispatch } from 'react-redux';
import { storageKeys } from '../../constants/storage';
import { handleLogout } from '../../utils/storage';

const { Title, Text } = Typography;
const ProfileTab = ({ profile, avatarUrl, onAvatarChange, onProfileUpdate, debouncedFetchAll }) => {
  const [editOpen, setEditOpen] = useState(false);
  const [openPhoneModal, setOpenPhoneModal] = useState(false);
  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [countryCode, setCountryCode] = useState("86");
  const [phone, setPhone] = useState("");
  const [smsCode, setSmsCode] = useState('');
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [emailCode, setEmailCode] = useState("");

  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [loadingNew, setLoadingNew] = useState(false);

  const getPhoneLength = () => {
    switch (String(countryCode)) {
      case "86":
        return 11; // China
      case "852":
        return 8; // Hong Kong
      default:
        return 11;
    }
  };

  const isValidPhone = () => {
    return phone?.length === getPhoneLength();
  };

  const fields = [
    { label: t('MemberID'), value: profile?.memberId },
    {
      label: t("Phone Number"),
      value: profile?.phone
        ? `+${profile.phone.countryCode} ${profile.phone.number}`
        : "-"
    },
    { label: t("Name"), value: profile?.name },
    { label: t("Email"), value: profile?.email },
    { label: t("Province"), value: profile?.province },
    { label: t("gender"), value: t(profile?.sex) },
    { label: t("Category"), value: t(profile?.category) },
    { label: t("City"), value: profile?.city },
  ];

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  useEffect(() => {
    if (profile?.phone) {
      setCountryCode(String(profile.phone.countryCode || "86"));
      setPhone(profile.phone.number || "");
    }
  }, [profile, openPhoneModal]);

  const handleClose = () => {
        if(loading) return;
        setOpenPhoneModal(false);
        setOpenEmailModal(false);
        setError(null);
        setSmsCode("");
        setEmailCode("");
        setPassword("");
        setConfirmPassword("");
        setOldPassword("");
        setLoading(false);
        setCountdown(0);
    };

  const saveAuth = (token, user, refreshToken) => {
    dispatch(loginSuccessAction({ token, user: user, photo: user?.picUrl }));
    localStorage.setItem(storageKeys.TOKEN, btoa(token));
    localStorage.setItem(storageKeys.TOKEN_RE, btoa(refreshToken));
    // localStorage.setItem(storageKeys.USER, btoa(JSON.stringify(user)));
    localStorage.setItem(storageKeys.USER, JSON.stringify(user));
  };

  const handlePhoneContinue = async () => {
    if (!isValidPhone() || smsCode.length !== 6) {
      message.error(t("Please enter a valid phone number and verification code"));
      return false;
    }
    setError(null);
    setLoading(true)

    try {
      const res = await httpClient.put(UPDATE_PHONE_URL, {
        "phone": {
          "countryCode": countryCode,
          "number": phone
        },
        "code": `${smsCode}`
      })
        .then(res => res.data)
        .catch(error => ({ status: 500, error }));
      if (res?.status === 200) {
        message.success(t("Phone number Updated Successfully"))
        debouncedFetchAll();
        handleClose();
      } else {
        setError(t(res?.error?.message  || res?.message === "This phone number has already been used" ? t("sms_used_phone_number"): "" || "Failed"));
        // message.error(t(res?.error?.message || res?.message || "Failed to login"));
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(t("Failed to login"));
      // message.error(t("Failed to login"));
    }
  };

  const handleSendCode = async () => {
    if (!phone || !isValidPhone() || countdown > 0) {
      return false;
    }
    try {
      setError(null);
      setLoadingNew(true)
      const res = await httpClient.post(OTP_URL, {
        "phone": {
          "countryCode": countryCode,
          "number": phone
        },
        memberId: profile?.memberId
      })
        .then(res => res.data)
        .catch(error => ({ status: 500, error }));
      if (res?.status === 200) {
        message.success(t(res?.message || "Verification code sent successfully"));
        setCountdown(60);
        setLoadingNew(false)
      } else {
        const errorMessage =
          res?.message === "This phone number has already been used"
            ? "sms_used_phone_number"
            : res?.error?.message ||
              res?.message ||
              "Failed to send verification code";
        message.error(t(errorMessage));
        setLoadingNew(false)
      }
    } catch (error) {
      message.error(t("Failed to send verification code"));
      setLoadingNew(false)
    }
  }

  const handleEmailSendCode = async () => {
    if (!email || countdown > 0) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      message.error(t("text_enter_valid_email"));
      return false;
    }
    try {
      setError(null);
      setLoadingNew(true);
      const res = await httpClient.post('/auth/request-otp-mail', { email, memberId: profile?.memberId })
        .then(res => ({ success: true, data: res.data }))
        .catch(error => ({ success: false, error: error?.response?.data || error }));
      if (res?.data?.success) {
        message.success(t(res.data?.message || "Verification code sent successfully"));
        setCountdown(60);
      } else {
        const errorMessage =
          res?.data?.message === "This email address has already been used"
            ? "sms_used_email"
            : res?.error?.message ||
              res?.error?.error ||
              "Failed to send verification code";
        message.error(t(errorMessage));
      }
    } catch (error) {
      message.error(t("Failed to send verification code"));
    } finally {
      setLoadingNew(false);
    }
  };

  const handleEmailContinueAction = async (actionType) => {
    // if (actionType === 'bindEmail' || actionType === 'resetPassword') {
    //     if (password !== confirmPassword) {
    //         setError(t("Passwords do not match"));
    //         return;
    //     }
    // }
    setLoading(true);
    setError(null);
    try {
        const payload = {
            email: email,
        };
        if (actionType === 'bindEmail' || actionType === 'changeEmail') {
            payload.code = emailCode;
        }
        // if (password) {
        //     payload.password = password;
        // }
        // if (actionType === 'resetPassword' && oldPassword) {
        //     payload.passwordCurrent = oldPassword;
        // }

        const res = await httpClient.put('/member/profile/email', payload)
            .then(res => ({ success: true, data: res.data }))
            .catch(error => ({ success: false, error: error?.response?.data || error }));

        if (res?.data?.status === 200) {
            message.success(t(res.data?.message || "Email Updated Successfully"));
            debouncedFetchAll();
            handleClose();
        } else {
            setError(t( res?.data?.message === "This email address is already bound to another account." ? t("sms_used_email") : res.error?.message || res.error?.error));
        }
    } catch (error) {
        setError(t("Failed to update email"));
    } finally {
        setLoading(false);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>{t("text_profile")}</Title>
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => setEditOpen(true)}
          style={{
            background: '#e2005a',
            borderColor: '#e2005a',
            borderRadius: 20,
            height: 40,
            paddingInline: 24,
          }}
        >
          {t("Edit Profile")}
        </Button>
      </div>

      {/* Avatar + name */}
      <div style={{ textAlign: 'center', margin: '28px 0px 32px' }}>
        <ProfileAvatar
          name={`${profile?.name}`}
          size={96}
          imageUrl={profile?.picUrl ? profile?.picUrl : avatarUrl}
          onImageChange={onAvatarChange}
          debouncedFetchAll={debouncedFetchAll}
        />
        <div style={{ fontWeight: 700, fontSize: 18, marginTop: 12 }}>
          {profile.name}
        </div>
        {/* <MemberCard profile={profile} onRefresh={debouncedFetchAll} /> */}
      </div>

      {/* Field grid */}
      <Row gutter={[24, 24]} style={{ marginTop: "20px" }}>
        {fields.map((f) => (
          <Col span={12} key={f.label}>
            <Text
              type="secondary"
              style={{ fontSize: 14, color: '#666' }}
            >
              {f.label}
            </Text>
            <div style={{ fontWeight: 500, fontSize: 16, marginTop: 4, display: 'flex', alignItems: 'center' }}>
              {/* Special rendering for Phone Number */}
              {f.label === t("Phone Number") || f.label === "Phone Number" || f.label === "手机号码" ? (
                  f.value !== "-" && f.value ? (
                    <>
                      {f.value}
                      <span onClick={() => setOpenPhoneModal(true)} style={{ marginLeft: "0.5rem", cursor: "pointer" }}>
                        <FaRegEdit size={16} color="#e2005a" />
                      </span>
                    </>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ background: '#f5f5f5', padding: '2px 8px', borderRadius: 12, display: 'flex', alignItems: 'center', marginRight: 12 }}>
                        <MobileOutlined style={{ color: '#999', marginRight: 6 }} />
                        <span style={{ color: '#999', fontSize: 13 }}>{t("text_nobind")}</span>
                      </div>
                      <span style={{ color: '#e5e5e5', marginRight: 12 }}>|</span>
                      <span style={{ color: '#e2005a', cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={() => setOpenPhoneModal(true)}>
                        + {t("button_bind_phone")}
                      </span>
                    </div>
                  )
              ) : 
              /* Special rendering for Email */
              f.label === t("text_email") || f.label === "Email" || f.label === "邮箱" ? (
                  f.value && f.value !== "-" ? (
                    <>
                      {f.value}
                      <span onClick={() => setOpenEmailModal(true)} style={{ marginLeft: "0.5rem", cursor: "pointer" }}>
                        <FaRegEdit size={16} color="#e2005a" />
                      </span>
                    </>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ background: '#f5f5f5', padding: '2px 8px', borderRadius: 12, display: 'flex', alignItems: 'center', marginRight: 12 }}>
                        <MailOutlined style={{ color: '#999', marginRight: 6 }} />
                        <span style={{ color: '#999', fontSize: 13 }}>{t("text_nobind")}</span>
                      </div>
                      <span style={{ color: '#e5e5e5', marginRight: 12 }}>|</span>
                      <span style={{ color: '#e2005a', cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={() => setOpenEmailModal(true)}>
                        + {t("button_bind_email")}
                      </span>
                    </div>
                  )
              ) : 
              /* Default rendering */
              (
                f.value || "-"
              )}
            </div>
          </Col>
        ))}
      </Row>
      {/* </Card> */}

      {/* Edit Modal */}
      <EditProfileModal
        open={editOpen}
        profile={profile}
        avatarUrl={avatarUrl}
        onClose={() => setEditOpen(false)}
        onSave={({ updatedProfile, updatedAvatar }) => {
          onProfileUpdate(updatedProfile);
          onAvatarChange(updatedAvatar);
        }}
        debouncedFetchAll={debouncedFetchAll}
      />

      <PhoneModal
        open={openPhoneModal}
        setOpen={setOpenPhoneModal}
        phone={phone}
        setPhone={setPhone}
        countryCode={countryCode}
        setCountryCode={setCountryCode}
        smsCode={smsCode}
        setSmsCode={setSmsCode}
        handleSendCode={handleSendCode}
        handlePhoneContinue={handlePhoneContinue}
        loading={loading}
        setError={setError}
        countdown={countdown}
        error={error}
        PhoneInput={PhoneInput}
        disabled={countdown > 0}
        handleClose={handleClose}
        t={t}
        profile={profile}
        loadingNew={loadingNew}
      />

      <EmailModal
        open={openEmailModal}
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        confirmPassword={confirmPassword}
        setConfirmPassword={setConfirmPassword}
        oldPassword={oldPassword}
        setOldPassword={setOldPassword}
        emailCode={emailCode}
        setEmailCode={setEmailCode}
        handleSendCode={handleEmailSendCode}
        handleEmailContinue={handleEmailContinueAction}
        loading={loading}
        setError={setError}
        error={error}
        countdown={countdown}
        t={t}
        handleClose={handleClose}
        loadingNew={loadingNew}
      />
    </>
  );
};

export default ProfileTab;
